<template>
  <div class="container-fluid">
    <Breadcrumb
        :values="{ Home: '/', Search: '/search', Study: '/study/' + $route.params.id, Mapping: ''}"></Breadcrumb>
    <div class="animated fadeIn">
      <AddQuestionPopup :mapping="data" :metrics="metrics" ref="question-popup"
                        v-on:question-added="searchMapping()"></AddQuestionPopup>
      <div class="row">
        <div class="col-12">
          <router-link :to="'/study/' + $route.params.id" class="main-category ml-1">
            <font-awesome-icon style="font-size: 0.9rem" icon="chevron-left"></font-awesome-icon>
            <font-awesome-icon class="mr-1" style="font-size: 0.9rem" icon="chevron-left"></font-awesome-icon>
            Back to study
          </router-link>
          <div v-for="(questionsObject, fileIndex) in data" :key="`file-${fileIndex}`" class="mt-4">
            <div class="questions-toggle d-flex justify-content-end align-items-center">
              <span class="main-category mr-2" :style="{color: showUnmapped ? '#20a8d8' : 'rgba(0, 0, 0, 0.15)'}">Show all questions</span>
              <ToggleButton class="toggle-switch" :labels="{checked: 'show', unchecked: 'hide'}"
                            :color="{checked: '#20a8d8', unchecked: 'rgba(0, 0, 0, 0.15)'}" v-model="showUnmapped"
                            :width="60"></ToggleButton>
            </div>
            <div class="row">
              <div class="col-8">
                <div class="d-flex justify-content-between align-items-center">
                  <h4 class="m-0">{{questionsObject.filename}}</h4>
                  <div>
                    <font-awesome-icon icon="plus" class="mr-1 add-button" color="#20a8d8"
                                       @click="$refs['question-popup'].showPopup(questionsObject.filename)"></font-awesome-icon>
                  </div>
                </div>
                <div class="card mb-0 mt-3">
                  <div class="card-header d-flex justify-content-between">
                    <div class="pagination-button" @click="pagination.currentPage !== 0 && changePage(-1)">
                      <font-awesome-icon icon="chevron-left"/>
                    </div>
                    <div class="d-flex justify-content-center align-items-center"><span>{{pagination.currentPage + 1}} / {{pagination.maxPages}}</span>
                    </div>
                    <div class="pagination-button"
                         @click="pagination.currentPage !== pagination.maxPages - 1 && changePage(1)">
                      <font-awesome-icon icon="chevron-right"/>
                    </div>
                  </div>
                </div>
                <div class="questions-container">
                  <div class="loading-wrapper" :class="{hidden: !pagination.loading}">
                    <Loader :show="pagination.loading" :fixed="false"/>
                  </div>
                  <template v-if="!pagination.loading">
                    <div v-for="(item, index) in questionsList[fileIndex]" :key="`mapping-${index}`" class="w-100">
                      <div class="card w-100 question mb-0"
                           :class="index === getSortedData(fileIndex).length - 1 ? 'question-border' : ''">
                        <div class="card-body pl-0 d-flex justify-content-center align-items-center">
                          <div v-if="item.domain.type === 'constant'"
                               class="delete-icon d-flex justify-content-center align-items-center">
                            <font-awesome-icon icon="trash-alt" color="#f86c6b"></font-awesome-icon>
                          </div>
                          <div class="question-id mx-2">
                            <span class="range-text">{{item.qid}}</span>
                          </div>
                          <div class="question-content d-flex justify-content-center align-items-center">
                            <div class="w-100 d-flex justify-content-between flex-column">
                        <span class="question-text pr-4">
                          {{item.text}}
                        </span>
                              <div class="mt-1 range-text">
                                <div v-if="item.domain.type === 'numerical'">
                                  <span class="mr-2">Numerical:</span>
                                  <span class="mr-1">{{item.domain.min}}</span> – <span
                                    class="ml-1">{{item.domain.max}}</span>
                                </div>
                                <div v-else-if="item.domain.type === 'categorical'">
                                  <div class="d-flex" @click="toggleCollapse(index)"
                                       :class="{'categorical-text': item.domain.values.length > 2}">
                                    <span class="mr-2">Categorical:</span>
                                    <div v-if="!collapses.includes(index)">
                                      {{getCategoricalValue(item.domain.values, 2)}}<span
                                        v-if="item.domain.values.length > 2">, ...</span>
                                    </div>
                                    <div v-else>
                                      {{getCategoricalValue(item.domain.values)}}
                                    </div>
                                  </div>
                                </div>
                                <div v-else-if="item.domain.type === 'constant'" class="constant-domain">
                                  <template v-if="!editableDomainFields.includes(item.id)">
                                    <span class="mr-2">Constant:</span>
                                    <span>{{item.domain.value}}</span>
                                  </template>
                                  <template v-else>
                                    <el-input
                                        size="small"
                                        class="tag-input"
                                        placeholder="Input domain data"
                                        v-model="item.domain.value"
                                        @keydown.native.esc="cancelDataEdit(item.id)"
                                        @keydown.native.enter="saveData(item.id, item)"
                                    />
                                  </template>
                                  <font-awesome-icon class="edit-icon ml-2"
                                                     :icon="editableDomainFields.includes(item.id) ? 'save' : 'edit'"
                                                     @click="toggleEdition(item)"></font-awesome-icon>
                                </div>
                                <div v-else>
                                  <span><i>No data</i></span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="mb-auto">
                            <el-select
                                v-model="item.metric"
                                placeholder="Select question" filterable
                                @change="[saveMapping(item), item.metric_validated = true]"
                                :disabled="item.metric_validated"
                                style="width: 250px;" clearable collapse-tags
                            >
                              <el-option-group label="Press Tab/Escape to exit">
                                <el-option
                                    v-for="metric in metrics"
                                    :key="metric.name" :label="metric.label" :value="metric.name"
                                    :disabled="!unusedMetrics(fileIndex).includes(metric.name)">
                                  <span style="float: left">{{metric.label}}</span>
                                </el-option>
                              </el-option-group>
                            </el-select>
                            <div v-if="item.suggested_metric" class="suggested-question-metric">
                              Suggested metric: {{getSuggestedValue(item.suggested_metric)}}
                            </div>
                          </div>
                          <div class="d-flex justify-content-center align-items-center"
                               style="padding-left: 1.25rem">
                            <button
                                class="search-button btn btn-block mt-0 d-flex justify-content-center align-items-center"
                                :class="{'btn-outline-success': item.metric_validated, 'btn-outline-dark': item.metric && !item.metric_validated, 'btn-outline-secondary': !item.metric && !item.metric_validated, disabled: !item.metric_validated}"
                                :disabled="!item.metric"
                                style="width: 35px; height: 35px;"
                                @click="[sendUpdateQuestionRequest(item, !item.metric_validated), item.metric_validated = !item.metric_validated]">
                              <font-awesome-icon v-if="item.metric_validated"
                                                 icon="check"></font-awesome-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              <div class="col-4">
                <div class="d-flex flex-column align-items-center w-100" style="margin-top: 41px; height: 74.7vh">
                  <div class="side-column card" style="margin-bottom: 0.4vh">
                    <div class="card-header" @click="validatedQuestionsOpened = !validatedQuestionsOpened">Validated
                      questions [{{validatedQuestions.length}}]
                    </div>
                    <b-collapse id="questions" v-model="validatedQuestionsOpened">
                      <div class="data card-body p-0">
                        <div class="validated-question d-flex flex-column px-2 py-1"
                             v-for="question in validatedQuestions" @click="toggleQuestion(question.id)"
                             :key="question.id">
                          <div style="width: 100%; min-height: 40px;" class="d-flex align-items-center">
                            {{question.text}}
                          </div>
                          <div class="w-100" v-if="questionOpened.includes(question.id)">
                            <div class="d-flex pt-1 px-1" style="font-size: 80%; border-top: 1px dashed #d1d4d7;">
                              <div style="width: 50%" class="d-flex justify-content-start align-items-center">
                                <span>ID: {{question.id}}</span>
                              </div>
                              <div style="width: 50%;" class="d-flex justify-content-end align-items-center">
                                <span style="text-transform: capitalize; text-align: end">Metric: {{question.metric || '-'}}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </b-collapse>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ToggleButton } from 'vue-js-toggle-button'

import studiesActions from '../store/modules/studies/actionTypes'
import _ from 'lodash'
import AddQuestionPopup from '../components/StudyMapping/AddQuestionPopup'
import generalActions from '../store/modules/general/actionTypes'
import Breadcrumb from '../components/shared/Breadcrumb'
import studiesGetters from '../store/modules/studies/getterTypes'
import Loader from '../components/shared/Loader'

export default {
  name: 'StudyMapping',
  components: { Loader, Breadcrumb, AddQuestionPopup, ToggleButton },
  data () {
    return {
      metrics: [],
      data: [],
      mapping: {},
      sortBy: {
        key: 'qid',
        desc: true
      },
      pagination: {
        currentPage: 0,
        questionPerPage: 25,
        maxPages: 0,
        loading: false
      },
      showAlert: false,
      showUnmapped: true,
      validatedQuestionsOpened: true,
      suggestedQuestionsOpened: true,
      recomputeToggle: false,
      questionsList: {},
      validatedQuestionsArray: [],
      editableDomainFields: [],
      questionOpened: [],
      collapses: []
    }
  },
  computed: {
    ...mapGetters({
      questions: studiesGetters.GET_QUESTIONS
    }),
    validatedQuestions () {
      return this.validatedQuestionsArray
    },
    unusedMetrics (index) {
      return (index) => {
        return this.metrics.filter(el => !this.appliedMetrics(index).includes(el.name)).map(el => el.name)
      }
    },
    appliedMetrics (index) {
      return (index) => {
        return [...new Set(this.data[index].questions.map(item => item.metric))]
      }
    },
    isLoading () {
      return this.pagination.loading
    }
  },
  methods: {
    ...mapActions({
      toggleLoading: generalActions.TOGGLE_LOADING,
      getMetrics: studiesActions.FETCH_METRICS,
      getQuestions: studiesActions.FETCH_QUESTIONS,
      updateQuestion: studiesActions.UPDATE_STUDY_MAPPING
    }),
    async saveMapping (item) {
      const query = {
        id: item.id,
        metric: item.metric
      }
      if (item.domain.type === 'constant') {
        query['value'] = item.domain.value
      }
      await this.updateQuestion({
        id: this.$route.params.id,
        query
      })
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const test = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', test)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', true)
      }
    },
    getSortedData (index) {
      const minVal = this.pagination.currentPage * this.pagination.questionPerPage
      const maxVal = (this.pagination.currentPage + 1) * this.pagination.questionPerPage
      const arrayCopy = _.cloneDeep(this.data[index].questions)
      if (this.showUnmapped) {
        this.pagination.maxPages = Math.ceil(arrayCopy.length / this.pagination.questionPerPage)
        return arrayCopy.slice(minVal, maxVal > arrayCopy.length ? arrayCopy.length : maxVal)
      }
      const filteredArray = arrayCopy.filter(el => el.metric)
      this.pagination.maxPages = Math.ceil(filteredArray.length / this.pagination.questionPerPage)
      return filteredArray.slice(minVal, maxVal > arrayCopy.length ? arrayCopy.length : maxVal)
    },
    changePage (value) {
      this.pagination.loading = true
      this.pagination.currentPage = this.pagination.currentPage + value
      this.setQuestionsList()
    },
    getCategoricalValue (values, length) {
      const sortedArray = values.map(el => el.name).sort((a, b) => a > b ? 1 : a < b ? -1 : 0)
      if (length) {
        return sortedArray.splice(0, length).join(', ')
      }
      return sortedArray.join(', ')
    },
    getSuggestedValue (values, length) {
      if (length) {
        return values.splice(0, length).join(', ')
      }
      return values.join(', ')
    },
    async toggleEdition (item) {
      if (this.editableDomainFields.includes(item.id)) {
        this.editableDomainFields.splice(this.editableDomainFields.indexOf(item.id), 1)
        await this.saveMapping(item)
        await this.searchMapping()
      } else {
        this.editableDomainFields.push(item.id)
      }
    },
    cancelDataEdit (id) {
      this.editableDomainFields.splice(this.editableDomainFields.indexOf(id), 1)
    },
    async saveData (item) {
      this.editableDomainFields.splice(this.editableDomainFields.indexOf(item.id), 1)
      await this.saveMapping(item)
      await this.searchMapping()
    },
    setQuestionsList () {
      this.data.forEach((el, index) => {
        this.questionsList[index] = this.getSortedData(index)
      })
      setTimeout(() => { this.pagination.loading = false }, 300)
    },
    async searchMapping () {
      await this.getQuestions(this.$route.params.id)
      this.data = _.cloneDeep(this.questions)
      this.setQuestionsList()
      if (!this.validatedQuestionsArray.length) {
        this.setValidatedQuestions()
      }
    },
    toggleCollapse (index) {
      if (this.collapses.includes(index)) {
        this.collapses.splice(this.collapses.indexOf(index), 1)
      } else {
        this.collapses.push(index)
      }
    },
    toggleQuestion (index) {
      if (this.questionOpened.includes(index)) {
        this.questionOpened.splice(this.questionOpened.indexOf(index), 1)
      } else {
        this.questionOpened.push(index)
      }
    },
    async sendUpdateQuestionRequest (metricObj, value) {
      const query = { ...metricObj, metric_validated: value }
      this.toggleLoading(true)
      await this.updateQuestion({
        id: this.$route.params.id,
        query
      })
      this.toggleLoading(false)
      if (value) {
        this.validatedQuestionsArray.push(query)
      } else {
        const questionObj = this.validatedQuestionsArray.find(el => el.id === metricObj.id)
        this.validatedQuestionsArray.splice(this.validatedQuestionsArray.indexOf(questionObj), 1)
      }
    },
    setValidatedQuestions (newData) {
      let questions = []
      const dataArray = newData && newData.length ? newData : this.data
      dataArray.forEach(el => {
        let data = el.questions.filter(el => {
          return el.metric_validated
        })
        questions = questions.concat(data)
      })
      this.validatedQuestionsArray = questions
    }
  },
  watch: {
    data (newData) {
      this.setValidatedQuestions(newData)
    },
    showUnmapped () {
      this.setQuestionsList()
    }
  },
  async mounted () {
    this.toggleLoading(true)
    this.metrics = await this.getMetrics()
    await this.searchMapping()
    this.toggleLoading(false)
  }
}
</script>

<style scoped>
.main-category {
  font-size: 17px;
}

.scroller {
  height: 100%;
}

h4 {
  color: #20a8d8;
  margin: 10px 20px;
}

.question-id {
  width: 10%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.question-content {
  width: 90%;
}

.questions-container {
  max-height: 74.7vh;
  overflow-y: scroll;
}

.questions-container::-webkit-scrollbar {
  display: none;
}

.question-text {
  font-size: 17px;
}

.range-text {
  color: #b0b3b9;
}

.questions-toggle {
  position: absolute;
  top: 0;
  right: 20px;
}

.questions-toggle >>> .vue-js-switch {
  margin: 0;
}

.vue-recycle-scroller__item-view .question {
  border-bottom: 0;
}

.question-border {
  border-bottom: 1px solid #d1d4d7 !important;
}

.categorical-text {
  cursor: pointer;
  transition: color 0.1s linear;
}

.categorical-text:hover {
  color: #9d9fa5;
}

.add-button {
  cursor: pointer;
  font-size: 1.2rem;
  transition: 0.1s linear;
}

.add-button:hover {
  transform: scale(1.1);
}

.delete-icon {
  width: 15px;
  height: 15px;
  cursor: pointer;
  position: absolute;
  left: 10px;
  top: 10px;
  opacity: 0;
  transition: opacity 0.1s linear;
}

.constant-domain .edit-icon {
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s linear;
}

.constant-domain:hover .edit-icon {
  opacity: 1;
}

.tag-input {
  width: 300px;
}

.card:hover .delete-icon {
  opacity: 1;
}

.validated-question {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d1d4d7 !important;
  width: 100%;
}

.side-column {
  margin-bottom: 0;
  width: 100%;
}

.data {
  overflow-y: scroll;
  height: calc(37vh - 46px);
}

.suggested-question-label {
  margin-top: 5px;
}

.suggested-question-content {
  overflow-y: scroll;
}

.suggested-question-metric {
  margin-top: 3px;
  margin-left: 3px;
  font-size: 12px;
  color: #d1d4d7;
}

.pagination-button {
  width: 15px;
  height: 25px;
  justify-content: center;
  align-items: center;
  display: flex;
  cursor: pointer;
}

.loader-wrapper {
  width: calc(100% - 30px);
  height: 74.7vh;
  position: absolute;
}

.loader-wrapper.hidden {
  z-index: -1;
}
</style>
